import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Mail } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useFetch, Card, Loader, PageHeader, Button, Field, Modal } from "../ui";

const WEB_ITEMS = ["Positioning clarity", "Homepage clarity", "Offer clarity", "Visual credibility", "Brand consistency", "Mobile experience", "Navigation", "Trust signals", "Testimonials", "Case studies", "Service presentation", "CTA strength", "Lead capture", "Conversion flow", "Contact experience", "SEO fundamentals", "Overall perceived value"];
const SOC_ITEMS = ["Profile clarity", "Brand consistency", "Visual identity", "Authority", "Offer clarity", "Content quality", "CTA", "Audience alignment", "Trust signals", "Social proof", "Path from social to website to inquiry"];
const RATINGS = ["Excellent", "Strong", "Average", "Weak", "Critical Gap"];
const CATS = [["brand_clarity", "Brand Clarity"], ["digital_authority", "Digital Authority"], ["conversion_readiness", "Conversion Readiness"], ["acquisition_health", "Acquisition Health"]];
const AUDIT_FIELDS = [["current_position", "Your Current Position"], ["whats_working", "What Is Already Working"], ["strongest_area", "Your Strongest Area"], ["biggest_opportunity", "Your Biggest Opportunity"], ["address_first", "What Should Be Addressed First"]];

const EMAIL_EVENTS = {
  booking_reminder_sent: (m) => `Booking reminder (${m.stage})`,
  hugh_nurture_sent: (m) => `H.U.G.H. nurture ${m.n}/4 — ${(m.topic || "").split(":")[0]}`,
  nurture_email_sent: (m) => `Long-term nurture ${m.n}/3`,
  followup_48h_sent: () => "Prescription follow-up (48h)",
  followup_5d_sent: () => "Prescription follow-up (5 days)",
  followup_10d_sent: () => "Prescription follow-up (10 days)",
  audit_call_booked: () => "Audit confirmation email",
  audit_approved: () => "Audit results email — score released",
  prescription_sent: () => "Prescription™ email",
};

const ReviewGrid = ({ items, value, onChange, prefix }) => (
  <div className="space-y-2">
    {items.map((it) => (
      <div key={it} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#1c1f25] bg-[#14161b] px-3 py-2.5">
        <span className="text-sm font-semibold text-white">{it}</span>
        <div className="flex flex-wrap gap-1">
          {RATINGS.map((r) => (
            <button key={r} data-testid={`${prefix}-${it.slice(0, 6)}-${r.slice(0, 4)}`} onClick={() => onChange({ ...value, [it]: r })}
              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${value[it] === r ? (r === "Critical Gap" ? "bg-red-500 text-white" : r === "Weak" ? "bg-amber-500 text-black" : "bg-[#C6FF00] text-[#0A0C0F]") : "bg-[#1c1f25] text-[#8A939B]"}`}>{r}</button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function AcquisitionLead() {
  const { id } = useParams();
  const { data: lead, loading, reload } = useFetch(`/admin/acquisition/leads/${id}`, [id]);
  const [webR, setWebR] = useState({});
  const [socR, setSocR] = useState({});
  const [webN, setWebN] = useState("");
  const [socN, setSocN] = useState("");
  const [adjust, setAdjust] = useState(null);
  const [reason, setReason] = useState("");
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [notes, setNotes] = useState({ highlight: "", upgrade: "", generate: "", harvest: "", general: "" });
  const [rx, setRx] = useState({ hugh_plan: {} });
  const rxPayload = () => ({ ...rx, hugh_plan: { highlight: notes.highlight || "", upgrade: notes.upgrade || "", generate: notes.generate || "", harvest: notes.harvest || "" } });
  const [packages, setPackages] = useState([]);
  const [brief, setBrief] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const previewPdf = async (newTab) => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/admin/acquisition/leads/${id}/brand-score-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (newTab) window.open(url, "_blank");
      else setPdfUrl(url);
    } catch (e) { toast.error(apiError(e)); }
    setPdfBusy(false);
  };

  const sendPdfNow = async () => {
    if (!window.confirm(`Email the Brand Score™ PDF to ${lead?.email} right now?`)) return;
    setPdfBusy(true);
    try {
      await api.post(`/admin/acquisition/leads/${id}/send-brand-score-pdf`);
      toast.success(`Brand Score PDF sent to ${lead.email}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    setPdfBusy(false);
  };

  const previewRxPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/admin/acquisition/leads/${id}/prescription-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(apiError(e)); }
    setPdfBusy(false);
  };

  useEffect(() => { api.get("/admin/acquisition/packages").then((r) => setPackages(r.data.packages)).catch(() => {}); }, []);

  useEffect(() => {
    if (!lead) return;
    setWebR(lead.website_review || {}); setSocR(lead.social_review || {});
    setWebN(lead.website_notes || ""); setSocN(lead.social_notes || "");
    setAudit(lead.audit || null);
    if (lead.call_notes) setNotes(lead.call_notes);
    if (lead.prescription) setRx({ hugh_plan: {}, ...lead.prescription });
  }, [lead]);

  if (loading || !lead) return <Loader />;
  const scores = lead.final_scores || lead.preliminary_scores;
  const pdfGates = [
    !lead.final_scores && "score confirmation",
    !lead.audit && "audit findings",
    !lead.website_review && "website review",
    !lead.social_review && "social review",
  ].filter(Boolean);

  const saveReview = async () => {
    try { await api.post(`/admin/acquisition/leads/${id}/review`, { website_review: webR, social_review: socR, website_notes: webN, social_notes: socN }); toast.success("Review saved"); } catch (e) { toast.error(apiError(e)); }
  };
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/acquisition/leads/${id}/score`, action === "adjust" ? { action, categories: adjust, reason } : { action });
      toast.success(action === "adjust" ? "Score adjusted — audit draft generating…" : "Score confirmed — audit draft generating…");
      setAdjust(null); setTimeout(reload, 4000); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const saveAudit = async () => { try { await api.put(`/admin/acquisition/leads/${id}/audit`, { sections: audit }); toast.success("Audit saved"); } catch (e) { toast.error(apiError(e)); } };
  const approve = async () => {
    setBusy(true);
    try { await saveAudit(); const r = await api.post(`/admin/acquisition/leads/${id}/approve-audit`); toast.success("Audit approved & released to lead"); reload(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const link = (url, label) => url ? <Button variant="ghost" as="a" href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer" data-testid={`open-${label.toLowerCase().replace(/ /g, "-")}`}>{label} <ExternalLink className="h-3.5 w-3.5" /></Button> : null;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Strategist Review" title={`${lead.first_name} ${lead.last_name} — ${lead.business_name}`}
        subtitle={`${lead.stage}${lead.time_status ? ` · ${lead.time_status}` : ""} · Opportunity: ${lead.opportunity_score}/100 (${lead.opportunity_class})`}
        actions={<div className="flex flex-wrap gap-2"><Button variant="ghost" data-testid="open-meeting-brief" onClick={async () => { try { const r = await api.get(`/admin/delivery/brief/audit/${id}`); setBrief(r.data.brief); } catch (e) { toast.error(apiError(e)); } }}>Meeting Brief</Button>{link(lead.website, "Open Website")}{link(lead.instagram, "Open Instagram")}{link(lead.other_social, "Open Other Social")}</div>} />

      <Modal open={!!brief} onClose={() => setBrief(null)} title="Audit Review Call — Meeting Brief" testid="audit-brief-modal">
        {brief && (
          <div className="space-y-2 text-sm">
            {[["Company", brief.company], ["Contact", brief.contact], ["Website", brief.website], ["Brand Score", brief.brand_score != null ? `${brief.brand_score}/100` : "—"],
              ["Opportunity", brief.opportunity], ["Goal", brief.goal], ["Timeline", brief.timeline], ["Budget / likely package", brief.likely_package],
              ["Lead source", brief.lead_source], ["Call", brief.call]].map(([k, v]) => (
              <p key={k}><span className="font-bold text-[#8A939B]">{k}:</span> <span className="text-white">{v || "—"}</span></p>
            ))}
            <div className="rounded-xl bg-[#14161b] p-3">
              <p className="text-xs font-bold uppercase text-red-400">Biggest weaknesses</p>
              {brief.biggest_weaknesses.map((w) => <p key={w.area} className="mt-1 text-[#c8cdd3]">{w.area} — {w.score}/100</p>)}
              <p className="mt-3 text-xs font-bold uppercase text-[#C6FF00]">Strongest areas</p>
              {brief.biggest_opportunities.map((w) => <p key={w.area} className="mt-1 text-[#c8cdd3]">{w.area} — {w.score}/100</p>)}
            </div>
            {brief.notes && <p className="rounded-xl bg-[#14161b] p-3 text-[#c8cdd3]">{brief.notes}</p>}
          </div>
        )}
      </Modal>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="p-5" data-testid="lead-profile">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Lead Profile</p>
          {[["Email", lead.email], ["Industry", lead.industry], ["Type", lead.business_type], ["Stage", lead.business_stage], ["Goal", lead.goal], ["Timeline", lead.timeline], ["Budget", lead.budget], ["Source", lead.lead_source], ["Channels", (lead.channels || []).join(", ")], ["Call", lead.call?.start_display || "NOT BOOKED"], ["Audit due", lead.audit_deadline ? new Date(lead.audit_deadline).toLocaleString() : "—"]].map(([k, v]) => (
            <p key={k} className="mt-1.5 text-sm"><span className="font-bold text-[#8A939B]">{k}:</span> <span className="text-white">{v || "—"}</span></p>
          ))}
          <p className="mt-3 rounded-xl bg-[#14161b] p-3 text-sm text-[#c8cdd3]"><span className="font-bold text-[#8A939B]">Primary obstacle:</span> {lead.obstacle || "—"}</p>
        </Card>

        <Card className="p-5 lg:col-span-2" data-testid="lead-scores">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Scores {lead.final_scores ? "(FINAL)" : "(PRELIMINARY)"}</p>
            <p className="text-2xl font-extrabold text-[#C6FF00]">{scores.overall}/100 <span className="text-xs text-[#8A939B]">{lead.classification}</span></p>
          </div>
          <div className="mt-4 space-y-2">
            {CATS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-44 shrink-0 text-xs font-bold text-[#8A939B]">{label}</span>
                <div className="h-2.5 flex-1 rounded-full bg-[#1c1f25]"><div className="h-full rounded-full bg-[#C6FF00]" style={{ width: `${(adjust ? adjust[k] : scores[k])}%` }} /></div>
                {adjust ? <input data-testid={`adjust-${k}`} type="number" min={0} max={100} value={adjust[k]} onChange={(e) => setAdjust({ ...adjust, [k]: Number(e.target.value) })} className="w-16 rounded-lg border border-[#23262c] bg-[#14161b] px-2 py-1 text-sm text-white" /> : <span className="w-10 text-right text-sm font-bold text-white">{scores[k]}</span>}
              </div>
            ))}
          </div>
          {lead.adjustment && <p className="mt-3 text-xs text-amber-400">Adjusted by {lead.adjustment.admin}: {lead.adjustment.reason} · Preliminary preserved: {lead.preliminary_scores.overall}/100</p>}
          {!lead.final_scores && lead.call && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {adjust ? (<>
                <Field label="Adjustment reason" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1" data-testid="adjust-reason" />
                <Button onClick={() => decide("adjust")} disabled={busy || !reason} data-testid="save-adjust-btn">Save Adjusted Score</Button>
                <Button variant="ghost" onClick={() => setAdjust(null)}>Cancel</Button>
              </>) : (<>
                <Button onClick={() => decide("confirm")} disabled={busy} data-testid="confirm-score-btn">Confirm Preliminary Score</Button>
                <Button variant="ghost" onClick={() => setAdjust({ ...Object.fromEntries(CATS.map(([k]) => [k, scores[k]])) })} data-testid="adjust-score-btn">Adjust Score</Button>
              </>)}
            </div>
          )}
          {!lead.call && <p className="mt-4 rounded-xl bg-amber-500/10 p-3 text-sm font-bold text-amber-400">No booked Audit Review Call — no strategist review. Preliminary score retained for lead intelligence only.</p>}
        </Card>
      </div>

      <Card className="p-5" data-testid="brand-score-pdf-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Brand Score™ PDF Report</p>
            <p className="mt-1 text-sm text-[#8A939B]">
              {lead.brand_score_pdf_sent
                ? <span className="font-bold text-[#C6FF00]">Sent to the lead {new Date(lead.brand_score_pdf_sent).toLocaleString()}</span>
                : pdfGates.length === 0
                  ? <span className="font-bold text-[#C6FF00]">Ready — auto-sends 10 minutes before the Audit Review Call</span>
                  : <span className="font-bold text-amber-400">Won't send yet — missing: {pdfGates.join(", ")}</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => previewPdf(false)} disabled={pdfBusy} data-testid="preview-pdf-btn">{pdfBusy ? "Working…" : pdfUrl ? "Refresh Preview" : "Preview PDF"}</Button>
            <Button variant="ghost" onClick={() => previewPdf(true)} disabled={pdfBusy} data-testid="open-pdf-tab-btn">Open in Tab</Button>
            {lead.prescription && <Button variant="ghost" onClick={previewRxPdf} disabled={pdfBusy} data-testid="preview-rx-pdf-btn">Preview Rx PDF</Button>}
            <Button onClick={sendPdfNow} disabled={pdfBusy || pdfGates.length > 0} data-testid="send-pdf-now-btn">{lead.brand_score_pdf_sent ? "Resend PDF Now" : "Send PDF Now"}</Button>
            {pdfUrl && <Button variant="ghost" onClick={() => setPdfUrl(null)} data-testid="close-pdf-btn">Close</Button>}
          </div>
        </div>
        {pdfUrl && <iframe title="Brand Score PDF preview" src={pdfUrl} className="mt-4 h-[620px] w-full rounded-xl border border-[#23262c] bg-white" data-testid="pdf-preview-frame" />}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Website Review</p>
          <ReviewGrid items={WEB_ITEMS} value={webR} onChange={setWebR} prefix="web" />
          <Field label="Website notes" as="textarea" rows={3} value={webN} onChange={(e) => setWebN(e.target.value)} className="mt-3" data-testid="web-notes" />
        </Card>
        <Card className="p-5">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Social Presence Review</p>
          <ReviewGrid items={SOC_ITEMS} value={socR} onChange={setSocR} prefix="soc" />
          <Field label="Social notes" as="textarea" rows={3} value={socN} onChange={(e) => setSocN(e.target.value)} className="mt-3" data-testid="soc-notes" />
        </Card>
      </div>
      <Button onClick={saveReview} data-testid="save-review-btn">Save Review</Button>

      {lead.final_scores && (
        <Card className="space-y-4 p-5" data-testid="audit-editor">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Audit Draft {lead.audit_approved ? "— APPROVED & RELEASED" : "(internal until approved)"}</p>
            {!lead.audit_approved && <Button variant="ghost" onClick={async () => { setBusy(true); try { const r = await api.post(`/admin/acquisition/leads/${id}/generate-draft`); setAudit(r.data.audit); toast.success("Draft regenerated"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } }} disabled={busy} data-testid="regen-draft-btn">{busy ? "Working…" : "Regenerate AI Draft"}</Button>}
          </div>
          {!audit && <p className="text-sm text-[#8A939B]">Draft is generating… refresh in a few seconds.</p>}
          {audit && (<>
            {AUDIT_FIELDS.map(([k, label]) => (
              <Field key={k} label={label} as="textarea" rows={2} value={audit[k] || ""} onChange={(e) => setAudit({ ...audit, [k]: e.target.value })} data-testid={`audit-${k}`} />
            ))}
            {(audit.growth_leaks || []).map((g, i) => (
              <Field key={i} label={`Growth Leak ${i + 1}`} as="textarea" rows={2} value={g} onChange={(e) => setAudit({ ...audit, growth_leaks: audit.growth_leaks.map((x, j) => j === i ? e.target.value : x) })} data-testid={`audit-leak-${i}`} />
            ))}
            <Field label="Questions for the Audit Review Call (one per line)" as="textarea" rows={3} value={(audit.call_questions || []).join("\n")} onChange={(e) => setAudit({ ...audit, call_questions: e.target.value.split("\n") })} data-testid="audit-questions" />
            <div className="flex gap-2">
              <Button variant="ghost" onClick={saveAudit} data-testid="save-audit-btn">Save Draft</Button>
              {!lead.audit_approved && <Button onClick={approve} disabled={busy} data-testid="approve-audit-btn">APPROVE AUDIT & RELEASE RESULTS</Button>}
            </div>
            {lead.audit_approved && <p className="text-sm text-[#C6FF00]">Results live at: {`${window.location.origin}/audit/results/${lead.results_token}`}</p>}
          </>)}
        </Card>
      )}

      {lead.audit_approved && (
        <Card className="space-y-4 p-5" data-testid="call-workspace">
          <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Audit Review Call Workspace — The H.U.G.H. Method {lead.call_completed_at && <span className="text-[#C6FF00]">· CALL COMPLETED</span>}</p>
          {[["highlight", "HIGHLIGHT — What's working? What makes the business valuable? Where is there momentum?"],
            ["upgrade", "UPGRADE — Where are perception, positioning, website, trust or conversion weak?"],
            ["generate", "GENERATE — Where should leads come from? What system is missing? How should traffic become inquiries?"],
            ["harvest", "HARVEST — How to get more value from existing attention, leads, customers, referrals, nurture, retention?"],
            ["general", "General call notes"]].map(([k, label]) => (
            <Field key={k} label={label} as="textarea" rows={2} value={notes[k] || ""} onChange={(e) => setNotes({ ...notes, [k]: e.target.value })} data-testid={`call-notes-${k}`} />
          ))}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={async () => { try { await api.put(`/admin/acquisition/leads/${id}/call-notes`, { ...notes, completed: false }); toast.success("Notes saved"); } catch (e) { toast.error(apiError(e)); } }} data-testid="save-call-notes-btn">Save Notes</Button>
            {!lead.call_completed_at && <Button onClick={async () => { try { await api.put(`/admin/acquisition/leads/${id}/call-notes`, { ...notes, completed: true }); toast.success("Call marked completed"); reload(); } catch (e) { toast.error(apiError(e)); } }} data-testid="complete-call-btn">Mark Call Completed</Button>}
            {lead.call && lead.status !== "NO_SHOW" && (
              <Button variant="ghost" data-testid="mark-noshow-btn"
                onClick={async () => { if (!window.confirm(`Mark ${lead.first_name} as a no-show? ${lead.email} will be permanently blocked from booking again.`)) return; try { await api.post(`/admin/acquisition/leads/${id}/no-show`); toast.success("Marked no-show — email blocked from booking"); reload(); } catch (e) { toast.error(apiError(e)); } }}>
                <span className="text-red-400">Mark No-Show</span>
              </Button>
            )}
            {lead.status === "NO_SHOW" && <span className="self-center rounded-full bg-red-500/10 px-3 py-1 text-xs font-extrabold text-red-400" data-testid="noshow-badge">NO-SHOW — BLOCKED</span>}
          </div>
        </Card>
      )}

      {lead.call_completed_at && (
        <Card className="space-y-4 p-5" data-testid="rx-builder">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">HughLaunch Prescription™ {lead.prescription_sent_at && <span className="text-[#C6FF00]">· SENT {lead.rx_viewed_at ? "· VIEWED" : ""}</span>}</p>
            <Button variant="ghost" disabled={aiBusy} data-testid="rx-ai-generate-btn"
              onClick={async () => {
                setAiBusy(true);
                try {
                  await api.put(`/admin/acquisition/leads/${id}/call-notes`, { ...notes, completed: false });
                  const r = await api.post(`/admin/acquisition/leads/${id}/prescription/ai-draft`);
                  setRx({ ...r.data.sections, hugh_plan: rx.hugh_plan || {} });
                  toast.success("Prescription drafted from Brand Score + call notes — review and edit before sending");
                } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
              }}>
              <span className="text-[#C6FF00]">{aiBusy ? "Generating…" : "✦ Generate with AI"}</span>
            </Button>
          </div>
          {[["where_you_are", "Where You Are"], ["where_you_want_to_go", "Where You Want To Go"], ["whats_working", "What Is Working"], ["holding_back", "What Is Holding You Back"], ["change_first", "What Needs To Change First"]].map(([k, label]) => (
            <Field key={k} label={label} as="textarea" rows={2} value={rx[k] || ""} onChange={(e) => setRx({ ...rx, [k]: e.target.value })} data-testid={`rx-${k}`} />
          ))}
          <p className="text-xs text-[#8A939B]">The H.U.G.H. Plan is pulled automatically from your Audit Review Call Workspace notes above.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">Recommended HughLaunch Solution</span>
              <select data-testid="rx-package" value={rx.recommended_package || ""} onChange={(e) => setRx({ ...rx, recommended_package: e.target.value })}
                className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-3 text-white">
                <option value="">— select package —</option>
                {packages.map((p) => <option key={p.slug} value={p.slug}>{p.name} — ${Number(p.amount).toLocaleString()}</option>)}
              </select>
            </label>
            <Field label="Timeline" value={rx.timeline || ""} onChange={(e) => setRx({ ...rx, timeline: e.target.value })} data-testid="rx-timeline" />
            <Field label="Optional add-ons" value={rx.addons || ""} onChange={(e) => setRx({ ...rx, addons: e.target.value })} data-testid="rx-addons" />
            <Field label="Expected business impact" value={rx.expected_impact || ""} onChange={(e) => setRx({ ...rx, expected_impact: e.target.value })} data-testid="rx-impact" />
          </div>
          <Field label="Why this recommendation fits" as="textarea" rows={2} value={rx.why_fit || ""} onChange={(e) => setRx({ ...rx, why_fit: e.target.value })} data-testid="rx-why" />
          <Field label="Next step note" value={rx.next_step || ""} onChange={(e) => setRx({ ...rx, next_step: e.target.value })} data-testid="rx-next" />
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={async () => { try { await api.post(`/admin/acquisition/leads/${id}/prescription`, { sections: rxPayload() }); toast.success("Prescription saved"); } catch (e) { toast.error(apiError(e)); } }} data-testid="save-rx-btn">Save Prescription</Button>
            <Button onClick={async () => { setBusy(true); try { await api.post(`/admin/acquisition/leads/${id}/prescription`, { sections: rxPayload() }); const r = await api.post(`/admin/acquisition/leads/${id}/prescription/send`); toast.success("Prescription sent to lead"); reload(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } }} disabled={busy || !rx.recommended_package} data-testid="send-rx-btn">Send Prescription™</Button>
          </div>
          {lead.rx_token && <p className="text-sm text-[#C6FF00]">Live at: {`${window.location.origin}/prescription/${lead.rx_token}`}</p>}
        </Card>
      )}

      <Card className="p-5" data-testid="lead-email-history">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Email Touchpoints</p>
        {(() => {
          const emails = (lead.activity || []).filter((a) => EMAIL_EVENTS[a.type]).slice().reverse();
          if (!emails.length) return <p className="text-sm text-[#8A939B]">No emails sent to this lead yet.</p>;
          return (
            <div className="space-y-2">
              {emails.map((a, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[#1c1f25] bg-[#14161b] px-4 py-2.5" data-testid={`email-touchpoint-${i}`}>
                  <Mail className="h-4 w-4 shrink-0 text-sky-400" />
                  <span className="flex-1 text-sm font-semibold text-white">{EMAIL_EVENTS[a.type](a.meta || {})}</span>
                  <span className="shrink-0 text-xs text-[#8A939B]">{new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>

      <Card className="p-5" data-testid="lead-activity">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Activity Timeline</p>
        <div className="space-y-1.5">
          {(lead.activity || []).slice().reverse().map((a, i) => (
            <p key={i} className="text-sm text-[#c8cdd3]"><span className="text-[#8A939B]">{new Date(a.at).toLocaleString()}</span> — <span className="font-bold text-white">{a.type.replace(/_/g, " ")}</span></p>
          ))}
        </div>
      </Card>
    </div>
  );
}
